import * as React from 'react'
import { Link, useNavigate, useLocation } from 'react-router'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { loginInputSchema } from '@/schemas/auth.schema'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState } from '@/components/PageState'
import { returnPath } from '@/auth/return-path'

/** Read a redirect target off router location state without unsafe casts. */
function resolveRedirect<S>(state: S): string {
  return returnPath(state, '/ops')
}

/** Turn an API/network failure into a message suitable for the sign-in form. */
function loginErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    if (error.status === 401) return 'Incorrect email or password.'
    return error.body.message ?? 'Unable to sign in. Please try again.'
  }
  return toErrorMessage(error)
}

/** Staff sign-in for the ops console. Redirects to the attempted route on success. */
export function OpsLoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [fieldError, setFieldError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setFieldError(null)
    setFormError(null)

    const parsed = loginInputSchema.safeParse({ email, password })
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Invalid credentials')
      return
    }

    setIsSubmitting(true)
    try {
      await login(parsed.data)
      navigate(resolveRedirect(location.state), { replace: true })
    } catch (error) {
      setFormError(loginErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell
      title="Staff sign-in"
      brandSuffix="ops"
      description={
        <>
          The PixelSign ops console. Customers sign in at{' '}
          <Link to="/login" className="text-link underline-offset-4 hover:underline">
            the dashboard
          </Link>
          ; signers use the link in their email.
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field
          label="Password"
          htmlFor="password"
          labelAside={
            <Link to="/ops/forgot-password" className="text-caption text-link underline-offset-4 hover:underline">
              Forgot password?
            </Link>
          }
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        {fieldError ? <ErrorState message={fieldError} /> : null}
        {formError ? <ErrorState message={formError} /> : null}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthShell>
  )
}
